import JsBarcode from 'jsbarcode';

/**
 * PGSS-5747: SKU barcode labels and find-by-scan. Agreed 18 Oct 2026: printed 4" x 6" on the label printer already
 * used for box labels, one label per page. The barcode is Code 128 of the SKU only (never the name or price), so a
 * printed label never goes out of date; the SKU and the product name are printed as text.
 */

/** Modules of white either side of the bars: Code 128 asks for at least 10. */
export const QUIET_ZONE = 10;

/** Printable ASCII only: Code 128 B can't encode anything else (an NBSP or "é" in legacy data). */
const ENCODABLE = /^[\x20-\x7E]+$/;

/** The Code 128 bar pattern of a SKU as "1" (bar) / "0" (space) modules, or '' when it can't be encoded. */
export function skuBars(sku) {
  const text = String(sku || '').trim();
  if (!text || !ENCODABLE.test(text)) return '';
  try {
    const target = {};
    JsBarcode(target, text, { format: 'CODE128' });
    return (target.encodings || []).map((e) => e.data).join('');
  } catch (e) {
    return '';
  }
}

/** Runs of bars as { x, w } in modules, offset by the quiet zone, for an SVG. */
export function barRects(bars) {
  const rects = [];
  let start = -1;
  for (let i = 0; i <= bars.length; i += 1) {
    if (bars[i] === '1' && start < 0) start = i;
    if (bars[i] !== '1' && start >= 0) {
      rects.push({ x: start + QUIET_ZONE, w: i - start });
      start = -1;
    }
  }
  return rects;
}

/** The products a label can be printed for: those with a SKU that encodes. */
export function labelProducts(products) {
  return (products || []).filter((p) => skuBars(p?.sku));
}

/** The label printer's resolution: thermal 4" x 6" printers print at 203 dots per inch. */
export const PRINTER_DPI = 203;
/** The widest the barcode may be on a 4in label (with its quiet zones). */
export const MAX_BARCODE_IN = 3.6;

/**
 * Each bar module as a whole number of printer dots (review of #2747): stretching to a fixed width gave 2.9 or 1.5
 * dots a module, which the printer rounds unevenly. The barcode is the widest whole-dot size that fits; null when even
 * one dot a module doesn't fit (a SKU far over 30 characters).
 */
export function barcodeWidthIn(modules) {
  const dots = Math.floor((MAX_BARCODE_IN * PRINTER_DPI) / modules);
  return dots >= 1 ? (modules * dots) / PRINTER_DPI : null;
}

/** The size as L x W x H, from the product row. */
export function sizeText(product) {
  return [product?.length, product?.width, product?.height]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean)
    .join(' x ');
}

/**
 * What a scan found: open the one product (live before archived), or say none has that SKU. A scanner types the
 * SKU and presses Enter; the backend matches it exactly, ignoring capitals.
 */
export function scanResult(matches, sku) {
  const shown = String(sku || '').trim().toUpperCase();
  const list = matches || [];
  const live = list.filter((m) => !m.archived);
  if (!list.length) return { productId: null, message: `No product has SKU ${shown}` };
  // A duplicate SKU (possible until PGSS-5744's unique index is in): say so rather than open one of them at random.
  if (live.length > 1) {
    return { productId: null, message: `${live.length} products have SKU ${shown}. Fix the duplicate in Settings, SKU Codes.` };
  }
  return { productId: (live[0] || list[0]).id, message: null };
}

/**
 * Who sees Scan SKU: the roles that can open the product a scan finds (/products/:id is admin, staff and sales). Every
 * role a user has counts, whether `role` is one name or a list (review of #2747).
 */
export function canScanSku(user) {
  const names = [].concat(user?.role || [], (user?.roles || []).map((r) => (typeof r === 'string' ? r : r?.name)));
  return names.some((n) => ['admin', 'super admin', 'staff', 'sales'].includes(String(n || '').trim().toLowerCase()));
}
